/**
 * zone-sessions.ts — sign out of EVERY zone this browser holds a session in, not just the one
 * Amplify happens to be pointed at (Q13, quality session 30 October 2026).
 *
 * ⭐ THE DEFECT THIS CLOSES. Amplify keeps each Cognito app client's tokens under that client's
 * own cookie key (`CognitoIdentityServiceProvider.<clientId>.…`), and since ADR-120 Amendment 4
 * each zone has its own client. `signOut()` clears only the CONFIGURED client, and `signIn()`
 * refuses — locally, before Cognito is asked — when the configured client already holds a
 * session. So with two tabs open, a tab pointed at AU signed out of AU, left a US session another
 * tab held untouched, and the next US sign-in in that tab failed with "There is already a signed
 * in user." (reproduced both directions on the apex, 31 October 2026, `e2e/cross-zone-signin.mjs`).
 * It "cleared by itself later" when that US session expired.
 *
 * ⭐⭐ HOW EVERY ZONE IS FOUND. The cookies name their own client ids, so the browser always knows
 * WHICH clients hold a session. To revoke one properly (Cognito RevokeToken, ADR-024 §7) Amplify
 * also needs that client's pool id — known for the build zone, the session's active zone, and
 * every zone the resolver has listed (`/portal/zone-config` returns the full list, cached here).
 * A client no known zone describes is still cleared: its cookies are removed directly. Nothing a
 * browser holds can survive this, known zone or not.
 */
import { signOut } from 'aws-amplify/auth';
import { configureAmplify } from '../amplify-config';
import { BUILD_ZONE, activeZone, type ZoneConfig } from './zone-session';

/** Every zone's identity, as `/portal/zone-config` last listed it. Not personal; not a secret. */
const ZONES_KEY = 'nexa.zones';
/** The prefix of every Amplify Cognito token cookie, in every zone. */
const COOKIE_PREFIX = 'CognitoIdentityServiceProvider.';

const complete = (z: Partial<ZoneConfig> | null | undefined): z is ZoneConfig =>
  !!z && !!z.userPoolId && !!z.userPoolClientId && !!z.portalApiBaseUrl;

/** Remember the platform's zone list (from the resolver), so sign-out can reach every zone. */
export function rememberZones(zones: unknown): void {
  if (!Array.isArray(zones)) return;
  const valid = zones.filter((z): z is ZoneConfig => complete(z as Partial<ZoneConfig>));
  if (!valid.length) return;
  try {
    window.localStorage.setItem(ZONES_KEY, JSON.stringify(valid));
  } catch {
    /* private mode — the cookie sweep below still clears every zone */
  }
}

/** Every zone this browser knows how to address, one per app client. */
export function knownZones(): ZoneConfig[] {
  const list: ZoneConfig[] = [];
  try {
    const raw = window.localStorage.getItem(ZONES_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (Array.isArray(parsed)) list.push(...parsed.filter((z) => complete(z as Partial<ZoneConfig>)));
  } catch {
    /* unreadable — the build and active zones below still apply */
  }
  for (const z of [activeZone(), BUILD_ZONE]) if (complete(z)) list.push(z);
  const byClient = new Map<string, ZoneConfig>();
  for (const z of list) if (!byClient.has(z.userPoolClientId)) byClient.set(z.userPoolClientId, z);
  return [...byClient.values()];
}

/** The raw names of every Cognito token cookie on this host, in every zone. */
function cognitoCookieNames(): string[] {
  if (typeof document === 'undefined') return [];
  return document.cookie
    .split(';')
    .map((c) => c.split('=')[0]?.trim() ?? '')
    .filter((n) => n.startsWith(COOKIE_PREFIX));
}

/** The app-client ids that currently hold a signed-in session on this host. */
export function clientsWithSessions(): string[] {
  const ids = new Set<string>();
  for (const name of cognitoCookieNames()) {
    const m = /^CognitoIdentityServiceProvider\.([^.]+)\.LastAuthUser$/.exec(name);
    if (m?.[1]) ids.add(m[1]);
  }
  return [...ids];
}

/**
 * Remove every Cognito token cookie, in every zone. The backstop for a client no known zone
 * describes, and for anything a sign-out could not clear.
 *
 * ⚠ THE RAW NAME, AND THE SAME PATH. Amplify's CookieStorage writes host-only cookies on path `/`
 * through js-cookie, which percent-encodes some characters in the name (an `@` in a username).
 * Expiring a cookie needs the name exactly as the browser stores it and the same path, so the
 * name is taken straight from `document.cookie`, never rebuilt.
 */
export function sweepCognitoCookies(): void {
  for (const name of cognitoCookieNames()) {
    document.cookie = `${name}=; Max-Age=0; path=/`;
  }
}

/**
 * Sign out of every zone that holds a session in this browser, then leave Amplify pointed at
 * `then` (default: the session's active zone).
 *
 * Each known zone is signed out through Amplify, which revokes its refresh token at Cognito and
 * clears its cookies; any session left after that (an unknown client) is removed by the sweep.
 * Never throws — `signOut()` swallows its own revoke failures and still clears the tokens.
 */
export async function signOutEveryZone(then?: ZoneConfig): Promise<void> {
  const zones = knownZones();
  const restore = then ?? activeZone();
  for (const clientId of clientsWithSessions()) {
    const zone = zones.find((z) => z.userPoolClientId === clientId);
    if (!zone) continue;
    try {
      configureAmplify(zone);
      await signOut();
    } catch {
      /* the sweep below removes whatever this left */
    }
  }
  sweepCognitoCookies();
  configureAmplify(restore);
}
